import { observer } from 'mobx-react-lite'
import { type IColumn, Avatar, Typography, Icon, type IIconsVector, Box, Tooltip } from 'shared/ui'
import { getLabelAsNumberInternationalFormat, formatPhoneNumberToInternational } from 'shared/lib'
import type {
  IResponseContactUnlinkedSource,
  IResponseContactUnlinkedConflictItem,
} from 'entities/Contacts/api/types'
import { IntegrationKey } from 'entities/Integrations'
import type { RowContactUnlinked } from 'entities/Contacts/model/Table/RowContactUnlinked'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { IntegrationsPath } from 'entities/ExportRouters'
import type { ContactsUnlinkedTableStore } from 'pages/contacts/pages/cleanup/ui/ContactsUnlinkedTable'
import { LinkContact, type LinkContactStore } from './LinkContact'

import styles from './styles.module.scss'

const Integration = (integration: string) => {
  switch (integration) {
    case 'salesmsg-hubspot':
      return IntegrationKey.hubspot
    case 'salesmsg-infusionsoft':
      return IntegrationKey.infusionsoft
    case 'salesmsg-activecampaign':
      return IntegrationKey.activecampaign
    case 'salesmsg-pipedrive':
      return IntegrationKey.pipedrive
    case 'salesmsg-salesforce':
      return IntegrationKey.salesforce
  }

  return null
}

const ContactRowCell = ({ sourceJSON }: { sourceJSON: string }) => {
  const source: IResponseContactUnlinkedSource = JSON.parse(sourceJSON)

  const colors = ['#6372A5', '#1D95F2', '#10D0AD', '#E13466', '#FFC422', '#FF9423', '#A233E1']
  const color = colors[+source.id % colors.length]

  const firstName = source.full_name?.split(' ')?.[0] || undefined
  const lastName = source.full_name?.split(' ')?.[1] || undefined

  const avatarInfo = {
    firstName,
    lastName,
    color,
    number: formatPhoneNumberToInternational(source.formatted_number),
    image: undefined,
  }

  const name =
    source.full_name ||
    source.email ||
    source.number ||
    formatPhoneNumberToInternational(source.formatted_number)

  return (
    <Box
      sx={{
        alignItems: 'center',
        height: '32px',
      }}
      gap={1}
      display='flex'
    >
      <Avatar info={avatarInfo} size={24} />
      <Typography ellipsis variant={'body-md-regular'} tag={'div'} ariaLabel={'name'}>
        {getLabelAsNumberInternationalFormat(name)}
      </Typography>
    </Box>
  )
}

const PhoneRowCell = ({ sourceJSON }: { sourceJSON: string }) => {
  const source: IResponseContactUnlinkedSource = JSON.parse(sourceJSON)

  return (
    <Typography variant={'body-md-regular'} tag={'div'} ariaLabel={'formatted_number'}>
      {source.formatted_number &&
        getLabelAsNumberInternationalFormat(
          formatPhoneNumberToInternational(source.formatted_number)
        )}
    </Typography>
  )
}

const IntegrationRowCell = observer(
  ({
    conflictItemsJSON,
    integrationKey,
    entities,
  }: {
    conflictItemsJSON: string
    integrationKey: string
    entities: IEntitiesStore
  }) => {
    const items: IResponseContactUnlinkedConflictItem[] = JSON.parse(conflictItemsJSON)
    const integrationIcon: IIconsVector | null = Integration(integrationKey)
    const integration = entities.integrationsStore.getIntegration(integrationIcon)
    const isConnected = integration?.isConnected
    const { can_manage_integrations } = entities.userPermissionsStore

    return (
      <Box
        sx={{
          alignItems: 'center',
          height: '32px',
        }}
        gap={1}
        display='flex'
      >
        {integrationIcon && (
          <span className={styles.icon}>
            <Icon icon={integrationIcon} fontSize={11} />
          </span>
        )}
        <Typography variant={'body-md-regular'} tag={'div'} ariaLabel={'matching'}>
          {`${items.length} matching`}
        </Typography>
        {integrationIcon && !isConnected && (
          <Tooltip
            label={
              <>
                Integration disconnected
                <br />
                {can_manage_integrations ? (
                  <a
                    href={`/${IntegrationsPath.root}/${integrationIcon}`}
                    target='_blank'
                    rel='noreferrer'
                  >
                    Reconnect
                  </a>
                ) : null}
              </>
            }
            placement='top'
          >
            <Icon icon='alertTriangleYellow' fontSize={16} />
          </Tooltip>
        )}
      </Box>
    )
  }
)

const LinkRowCell = ({
  sourceJSON,
  conflictItemsJSON,
  integration,
  linkContactStore,
}: {
  sourceJSON: string
  conflictItemsJSON: string
  integration: string
  linkContactStore: LinkContactStore
}) => {
  const contact: IResponseContactUnlinkedSource = JSON.parse(sourceJSON)
  const matchedContacts: IResponseContactUnlinkedConflictItem[] = JSON.parse(conflictItemsJSON)
  const contactIntegration: IntegrationKey | null = Integration(integration)

  return (
    <LinkContact
      contact={contact}
      matchedContacts={matchedContacts}
      integration={contactIntegration}
      store={linkContactStore}
    />
  )
}

export const columns = (store: ContactsUnlinkedTableStore): IColumn<RowContactUnlinked>[] => {
  return [
    {
      field: 'name',
      name: 'Name',
      fullName: 'Name',
      renderRowCell: (row) => <ContactRowCell sourceJSON={row.origin.source} />,
      maxWidth: 260,
    },
    {
      field: 'number',
      name: 'Phone',
      renderRowCell: (row) => <PhoneRowCell sourceJSON={row.origin.source} />,
      maxWidth: 210,
    },
    {
      field: 'integration',
      name: 'Integration',
      renderRowCell: (row) => (
        <IntegrationRowCell
          conflictItemsJSON={row.origin.conflict_items}
          integrationKey={row.origin.direction}
          entities={store.entities}
        />
      ),
      maxWidth: 560,
    },
    {
      field: 'actions',
      name: ' ',
      renderRowCell: (row) => (
        <Box
          sx={{
            alignItems: 'center',
            justifyContent: 'flex-end',
            height: '32px',
          }}
          gap={1}
          display='flex'
        >
          <LinkRowCell
            sourceJSON={row.origin.source}
            conflictItemsJSON={row.origin.conflict_items}
            integration={row.origin.direction}
            linkContactStore={store.linkContactStore}
          />
        </Box>
      ),
    },
  ]
}
